import { useSession } from '@/shared/hooks/useSession'
import { apiClient } from '@/api/apiClient'
import axios, { isAxiosError } from 'axios'
import { useCallback, useState } from 'react'

type DownloadResult = {
  success: boolean
  errorMessage?: string
}
type UploadResult = {
  success: boolean
  errorMessage?: string
  fileName?: string
}

const ERRORS = {
  FORBIDDEN: 'FORBIDDEN',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  FAILED_TO_GENERATE_UPLOAD_URL: 'FAILED_TO_GENERATE_UPLOAD_URL',
  FAILED_TO_GENERATE_DOWNLOAD_URL: 'FAILED_TO_GENERATE_DOWNLOAD_URL',
  FAILED_TO_UPLOAD_FILE: 'FAILED_TO_UPLOAD_FILE',
  FAILED_TO_DOWNLOAD_FILE: 'FAILED_TO_DOWNLOAD_FILE',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE'
}

const ALLOWED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'application/pdf',
  'application/xml',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]

function validateFileType(fileType: string) {
  return ALLOWED_FILE_TYPES.includes(fileType)
}

const useOrganizationDocuments = () => {
  const [documentUploadState, setDocumentUploadState] = useState<
    'error' | 'uploading' | null | 'success'
  >(null)

  const { session } = useSession()

  const resetDocumentUploadState = useCallback(() => {
    setDocumentUploadState(null)
  }, [setDocumentUploadState])

  const uploadToBucket = async (file: File) => {
    if (!validateFileType(file.type)) {
      throw new Error(ERRORS.INVALID_FILE_TYPE)
    }

    let signedUploadUrl: string | null = null
    let normalizedKey: string | null = null
    let errorMessage: string | null = null
    try {
      const response = await apiClient.post<{ url: string; normalizedKey: string }>(
        `/v1/organizations/${session.organization.id}/generate-upload-url`,
        {
          key: file.name
        }
      )

      signedUploadUrl = response.data.url
      normalizedKey = response.data.normalizedKey
    } catch {
      errorMessage = ERRORS.FAILED_TO_GENERATE_UPLOAD_URL
    }

    if (!signedUploadUrl || !normalizedKey) {
      return
    }

    try {
      await axios.put(signedUploadUrl, file, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response?.status === 403) {
          errorMessage = ERRORS.FORBIDDEN
        } else if (err.response?.status === 413) {
          errorMessage = ERRORS.FILE_TOO_LARGE
        } else {
          errorMessage = ERRORS.FAILED_TO_UPLOAD_FILE
        }
      }
    }

    if (errorMessage) {
      throw new Error(errorMessage)
    }

    return normalizedKey
  }

  const uploadDocument = async (file?: File): Promise<UploadResult> => {
    if (!file) {
      return { success: false, errorMessage: ERRORS.FAILED_TO_UPLOAD_FILE }
    }

    setDocumentUploadState('uploading')
    try {
      const normalizedKey = await uploadToBucket(file)

      if (!normalizedKey) {
        return { success: false, errorMessage: ERRORS.FAILED_TO_UPLOAD_FILE }
      }

      setDocumentUploadState('success')

      return { success: true, fileName: normalizedKey }
    } catch (error: any) {
      let errorMessage = ERRORS.FAILED_TO_DOWNLOAD_FILE

      if (error.message) {
        errorMessage = error.message
      }

      setDocumentUploadState('error')
      return { success: false, errorMessage }
    }
  }

  const generateDownloadUrl = async (fileName: string) => {
    let downloadUrl: string | null = null
    let errorMessage: string | null = null
    try {
      const response = await apiClient.post<{ url: string }>(
        `/v1/organizations/${session.organization.id}/generate-download-url`,
        {
          key: fileName
        }
      )

      downloadUrl = response.data.url
    } catch {
      errorMessage = ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL
    }

    if (!downloadUrl) {
      return
    }
    if (errorMessage) {
      throw new Error(errorMessage)
    }

    return downloadUrl
  }

  const downloadDocument = async (fileName: string): Promise<DownloadResult> => {
    try {
      const downloadUrl = await generateDownloadUrl(fileName)
      if (!downloadUrl) {
        return { success: false, errorMessage: ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL }
      }

      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      return { success: true }
    } catch (error: any) {
      if (error.message === ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL) {
        return { success: false, errorMessage: ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL }
      }
      return { success: false, errorMessage: ERRORS.FAILED_TO_DOWNLOAD_FILE }
    }
  }

  return {
    resetDocumentUploadState,
    uploadToBucket,
    uploadDocument,
    generateDownloadUrl,
    downloadDocument,
    documentUploadState
  }
}

export default useOrganizationDocuments
